<script lang="ts">
  import LL, { locale } from '$i18n/i18n-svelte';
  import { getTranslatedCharacterName } from '$lib/er/character/translations';
  import { isNormal } from '$lib/er/season/season';
  import type ERAR from '$lib/erar/spec';
  import { Copy, ExternalLink, LinkIcon } from 'lucide-svelte';
  import { durationToString, trimTrailingZeroWithSign } from '$lib/utils/number';
  import { getQueueTypeName } from '$lib/er/queue';
  import RawWeaponImage from '../item/raw-weapon-image.svelte';
  import RawTraitImage from '../trait/raw-trait-image.svelte';
  import RawVskillImage from '../vskill/raw-vskill-image.svelte';
  import { getSubcategoryNameByIndex } from '$lib/er/item/translation';
  import { getAreaNameById } from '$lib/er/area/translations';
  import { indexToSubcategory, weaponSubcategories } from '$lib/er/item/stores';
  import { xs } from '$lib/utils/media-query';
  import RawInfusionImage from '$lib/er/infusion/raw-infusion-image.svelte';
  import CharacterIcon from '../character/character-icon.svelte';
  import Equipment from '../item/equipment.svelte';
  import { CharacterImageType } from '../character/consts';
  import { getHumanTimeDistanceToNowStrict } from '$lib/utils/locale-date';

  export let match: ERAR.Schema.MatchParticipation;

  let rankColor: 'common' | 'epic' | 'legendary' | 'escape' | 'win' | 'lose' = 'common';
  $: {
    if (match.maxRank === 2) {
      rankColor = match.rank === 1 ? 'win' : 'lose';
    }

    if (match.rank === 1) {
      rankColor = 'legendary';
    } else if (match.rank < match.maxRank / 2) {
      rankColor = 'epic';
    }
  }
</script>

<section class="match-summary" data-rank-color={rankColor}>
  <div class="meta">
    <span class="rank" data-rank-color={rankColor}>
      {#if match.maxRank === 2}
        {#if match.rank === 1}
          {$LL.glossary.win()}
        {:else}
          {$LL.glossary.lose()}
        {/if}
      {:else}
        #{match.rank}<span>/{match.maxRank}</span>
      {/if}
    </span>
    <span
      >{isNormal(match.seasonId) ? $LL.glossary.gameType.normal() : $LL.glossary.gameType.rank()}
      {getQueueTypeName($LL, match.queueType)}</span
    >
    <span>{durationToString(match.duration)}</span>
    <span>{getHumanTimeDistanceToNowStrict(match.matchFinishedAt, $locale)}</span>
  </div>
  <div class="character-container">
    <CharacterIcon
      id={match.characterId}
      skinIndex={match.characterSkinIndex}
      type={CharacterImageType.Lobby}
    >
      <svelte:fragment slot="level">{match.level}</svelte:fragment>
      <svelte:fragment slot="weapon">
        {@const weaponSubcategory = $indexToSubcategory.get(match.itemSubcategoryIndex)}
        {#if weaponSubcategory}
          <RawWeaponImage weaponSubcategoryId={weaponSubcategory.id} />
        {/if}
      </svelte:fragment>
    </CharacterIcon>
    <span class="weapon-character-name"
      >{getSubcategoryNameByIndex($LL, match.itemSubcategoryIndex)}
      {getTranslatedCharacterName($LL, match.characterId)}</span
    >
  </div>
  <section class="stats">
    <div class="stat kda">
      <h3>K·D·A</h3>
      <div class="kda-content">
        <span class="kill">{match.kill}</span><span class="death">{match.death}</span><span
          >{match.assist}</span
        >
      </div>
    </div>
    <div class="stat">
      <h3>
        {$LL.players.detail.totalDeal()}
      </h3>
      <span>
        {match.totalDeal}
      </span>
    </div>
    {#if !isNormal(match.seasonId)}
      {@const rpDelta = (match.afterMMR ?? 0) - match.recordedMMR}
      <div class="stat">
        <h3>
          {$LL.players.detail.rp()}
        </h3>
        <span>
          {match.afterMMR}
          <span class:plus={rpDelta > 0} class:minus={rpDelta < 0}>
            {trimTrailingZeroWithSign(rpDelta, 0)}
          </span>
        </span>
      </div>
    {/if}
  </section>
  <section class="items">
    {#if match.preferences.traits}
      <section class="traits">
        <RawTraitImage width="22" traitId={match.preferences.traits.firstCore} />
        {#if match.preferences.traits.secondSub.length > 0}
          {#if match.queueType === '4'}
            <RawInfusionImage width="22" infusionId={match.preferences.traits.secondSub[0]} />
          {:else}
            <RawTraitImage width="22" traitId={match.preferences.traits.secondSub[0]} asCategory />
          {/if}
        {/if}
        {#if match.spellId}
          <div class="vskill">
            <RawVskillImage width="22" skillId={match.spellId} />
            <span class="vskill-level">{match.spellLevel ?? '?'}</span>
          </div>
        {/if}
      </section>
    {/if}
    <Equipment itemIds={match.preferences.equipments} size={$xs ? 5 : 4} />
  </section>
  <section class="ids">
    <span class="version">{match.matchVersion}</span>
    <button>
      <span>{$LL.players.detail.route()}</span>
      {#if match.route != undefined}
        {match.route.id}
        {#if match.route.preferences.areas.length > 0}
          <span class="dimmed"
            >{$LL.players.detail.startArea({
              area: getAreaNameById($LL, match.route.preferences.areas[0]),
            })}</span
          >
        {/if}
        <!-- <ExternalLink size="12" /> -->
      {:else}
        {$LL.players.detail.noRouteIdOfStart()}
      {/if}
    </button>
    <button
      class="match-id"
      on:click={() => navigator.clipboard.writeText(match.matchId.toString())}
    >
      <span>{$LL.players.detail.matchId()}</span>
      {match.matchId}
      <Copy size="12" />
    </button>
  </section>
</section>

<style lang="postcss">
  @import '$lib/styles/breakpoints.css';

  .match-summary {
    flex: 1;
    padding: var(--space-3);
    display: grid;
    grid-template:
      'meta  meta' auto
      'image stats' auto
      'items items' auto
      'ids   ids  ' auto
      / 5.4rem 1fr;
    place-content: center;
    border-radius: var(--radius-2);

    gap: var(--space-3);

    :global(.character-image) {
      width: 5rem;
      margin-top: -1rem;
    }

    &[data-rank-color] {
      background: linear-gradient(
          110deg,
          hsla(var(---gradient-hsl), 0.08) 0%,
          hsla(var(---gradient-hsl), 0) 100%
        ),
        radial-gradient(
          circle at 0px 0px,
          hsla(var(---gradient-hsl), 0.15) 0%,
          hsla(var(---gradient-hsl), 0) 30%
        ),
        var(--elevation-1);
    }
    &[data-rank-color='epic'] {
      ---gradient-hsl: var(--color-item-epic-dimmed-hsl);
    }
    &[data-rank-color='legendary'] {
      ---gradient-hsl: var(--color-item-legendary-dimmed-hsl);
    }
    &[data-rank-color='common'] {
      /* color: var(--text-item-common); */
    }
    &[data-rank-color='win'] {
      ---gradient-hsl: var(--color-item-uncommon-dimmed-hsl);
    }
    &[data-rank-color='lose'] {
      /* ---gradient-hsl: var(--color-item-common-dimmed-hsl); */
    }
    &[data-rank-color='escpae'] {
      ---gradient-hsl: var(--color-item-rare-dimmed-hsl);
    }
  }

  .character-container {
    grid-area: image;
    margin-left: 0.4rem;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .meta {
    grid-area: meta;
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .rank {
    font-weight: bold;
    font-size: var(--size-7);

    &[data-rank-color='epic'] {
      color: var(--text-item-epic);
    }
    &[data-rank-color='legendary'] {
      color: var(--text-item-legendary);
    }
    &[data-rank-color='common'] {
      color: var(--text-item-common);
    }
    &[data-rank-color='win'] {
      color: var(--plus);
    }
    &[data-rank-color='lose'] {
      color: var(--minus);
    }
    &[data-rank-color='escpae'] {
      color: var(--plus);
    }

    span {
      color: var(--mauve-a8);
      /* font-size: var(--size-5); */
    }
  }

  .stats {
    grid-area: stats;
    display: flex;
    align-items: center;
    gap: var(--space-6);
    margin-left: var(--space-4);
    justify-self: stretch;
  }
  .stat {
    display: flex;
    flex-direction: column;
    /* gap: 0.2rem; */

    h3 {
      font-weight: var(--weights-normal);
      padding: 0;
      margin: 0;
    }

    span {
      font-size: var(--size-6);
      font-weight: var(--weights-bold);
    }
    .kda-content span {
      font-size: var(--size-8);
    }

    &.kda {
      margin-right: var(--space-3);
      margin-left: var(--space-5);
    }
    .kda-content {
      display: flex;

      span:not(:last-child)::after {
        content: '·';
        color: var(--mauve-8);
        margin: 0 var(--space-1);
      }
    }
  }

  .ids {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    grid-area: ids;

    button {
      padding: var(--space-2) var(--space-3);
      border-radius: var(--radius-2);
      background-color: transparent;
      display: flex;
      gap: var(--space-3);

      span {
        color: var(--text-dimmed);
      }

      &:hover {
        background-color: var(--text);
        color: var(--mauve-1);
      }
    }

    .match-id {
      /* display: none;
      
      @media (--sm) {
        display: block;
      } */
    }
  }

  .items {
    grid-area: items;
    display: flex;
    gap: var(--space-3);
    align-items: center;
  }

  .traits {
    height: 3rem;
    display: flex;
    gap: var(--space-2);
    padding: var(--space-2);
    background-color: var(--elevation-2);
    border-radius: var(--radius-full);
  }

  .weapon-character-name {
    text-align: center;
  }

  .vskill {
    position: relative;

    .vskill-level {
      position: absolute;
      right: -2px;
      bottom: -4px;
      padding: 1px var(--space-1);
      border-radius: var(--radius-full);
      background-color: var(--elevation-2);
    }
  }

  .version {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-2);
    background-color: var(--mauve-a3);
  }

  .death {
    color: var(--death);
  }
</style>
